import { centsToFormattedDollars } from '@fruitful/fruitils';
import { type RuleSetDisplay, RuleType } from '@fruitful/types';
import { BanknoteArrowDown, CreditCard, Receipt } from 'lucide-react-native';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import Animated, {
    Easing,
    interpolateColor,
    useAnimatedStyle,
    useDerivedValue,
    useSharedValue,
} from 'react-native-reanimated';
import {
    BodySmallRegular,
    Icon,
    LabelEmphasized,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { OnboardingMoneyMapPreviewFork } from './OnboardingMoneyMapPreviewFork';
import {
    getDebtData,
    getRuleIcon,
    GOALS_FILLED,
} from './onboardingMoneyMapUtils';

export interface SystemBoxLabelProps {
    maxLabelWidth: number;
    updateLabelWidth: (index: number, width: number) => void;
    updateLabelHeight: (key: string, height: number, yInRow: number) => void;
}

export const IncomeSystemContent = ({
    maxLabelWidth,
    updateLabelWidth,
    updateLabelHeight,
    incomeTotalCents,
    loopProgressA,
    loopProgressB,
    activeLoopIdx,
    disableAnimations = false,
}: SystemBoxLabelProps & {
    incomeTotalCents: number;
    loopProgressA: Animated.SharedValue<number>;
    loopProgressB: Animated.SharedValue<number>;
    activeLoopIdx: Animated.SharedValue<number>;
    disableAnimations?: boolean;
}) => {
    const [containerWidth, setContainerWidth] = useState(0);

    const activeProgress = useDerivedValue(() =>
        activeLoopIdx.value === 0 ? loopProgressA.value : loopProgressB.value,
    );

    const leftBarStyle = useAnimatedStyle(() => {
        const t = Math.min(activeProgress.value, 1);
        return {
            width: (1 - t) * containerWidth,
        };
    });

    const rightBarStyle = useAnimatedStyle(() => {
        const t = Math.min(activeProgress.value, 1);
        return {
            width: containerWidth,
            transform: [{ translateX: (1 - t) * containerWidth }],
        };
    });

    const incomeLabel = centsToFormattedDollars(incomeTotalCents, false);
    const incomeBarColor = disableAnimations ? '#F3F4F6' : '#F6DC72';

    return (
        <XStack width="100%" alignItems="center">
            <View
                backgroundColor="#FEFAEA"
                padding={6}
                borderRadius={4}
                alignItems="center"
                justifyContent="center"
                minWidth={maxLabelWidth > 0 ? maxLabelWidth : undefined}
                onLayout={(e) => {
                    updateLabelWidth(0, e.nativeEvent.layout.width);
                    updateLabelHeight(
                        'income',
                        e.nativeEvent.layout.height,
                        e.nativeEvent.layout.y,
                    );
                }}
            >
                <LabelEmphasized
                    textTransform="uppercase"
                    lineHeight={13}
                    letterSpacing={0.5}
                >
                    income
                </LabelEmphasized>
            </View>
            <OnboardingMoneyMapPreviewFork
                height={0}
                prongs={1}
                animated
                loopProgress={activeProgress}
            />
            <YStack
                flex={1}
                marginLeft={8}
                borderRadius={16}
                backgroundColor="$white"
                gap={8}
                padding={8}
                overflow="hidden"
            >
                <XStack gap={6} alignItems="center">
                    <Icon icon={BanknoteArrowDown} size={16} />
                    <BodySmallRegular>Direct deposit</BodySmallRegular>
                </XStack>
                <View
                    height={32}
                    borderRadius={8}
                    onLayout={(e) => {
                        setContainerWidth(e.nativeEvent.layout.width);
                    }}
                >
                    {containerWidth > 0 && (
                        <View
                            style={{
                                flexDirection: 'row',
                                width: containerWidth,
                                height: '100%',
                                position: 'relative',
                                overflow: 'hidden',
                                borderRadius: 8,
                            }}
                        >
                            <Animated.View
                                style={[
                                    {
                                        height: '100%',
                                        backgroundColor: incomeBarColor,
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                    },
                                    leftBarStyle,
                                ]}
                            >
                                <View
                                    height="100%"
                                    width={containerWidth}
                                    alignItems="flex-start"
                                    justifyContent="center"
                                    paddingHorizontal={12}
                                >
                                    <LabelEmphasized
                                        textTransform="uppercase"
                                        lineHeight={13}
                                        letterSpacing={0.5}
                                    >
                                        {incomeLabel}
                                    </LabelEmphasized>
                                </View>
                            </Animated.View>
                            {!disableAnimations && (
                                <Animated.View
                                    style={[
                                        {
                                            height: '100%',
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            backgroundColor: incomeBarColor,
                                            borderRadius: 8,
                                            alignItems: 'flex-start',
                                            justifyContent: 'center',
                                            paddingHorizontal: 12,
                                        },
                                        rightBarStyle,
                                    ]}
                                >
                                    <LabelEmphasized
                                        textTransform="uppercase"
                                        lineHeight={13}
                                        letterSpacing={0.5}
                                    >
                                        {incomeLabel}
                                    </LabelEmphasized>
                                </Animated.View>
                            )}
                        </View>
                    )}
                </View>
            </YStack>
        </XStack>
    );
};

export const MonthlyExpensesSystemContent = ({
    billsTotalCents,
    spendTotalCents,
    maxLabelWidth,
    updateLabelWidth,
    updateLabelHeight,
    braceHeight,
    setBraceHeight,
    highlightedSubBox,
    loopProgressA,
    loopProgressB,
    activeLoopIdx,
    forkTriggerThreshold,
    goalForkRange,
    barFillRange,
}: SystemBoxLabelProps & {
    billsTotalCents: number;
    spendTotalCents: number;
    braceHeight: number;
    setBraceHeight: (h: number) => void;
    highlightedSubBox?: 'core' | 'spend' | null;
    loopProgressA?: Animated.SharedValue<number>;
    loopProgressB?: Animated.SharedValue<number>;
    activeLoopIdx?: Animated.SharedValue<number>;
    forkTriggerThreshold?: number;
    goalForkRange?: number;
    barFillRange?: number;
}) => {
    const animated =
        loopProgressA != null && loopProgressB != null && activeLoopIdx != null;

    const activeProgress = useDerivedValue(() => {
        if (!animated) return 0;
        return activeLoopIdx.value === 0
            ? loopProgressA.value
            : loopProgressB.value;
    });

    const coreOpacity =
        highlightedSubBox != null && highlightedSubBox !== 'core' ? 0.5 : 1;
    const spendOpacity =
        highlightedSubBox != null && highlightedSubBox !== 'spend' ? 0.5 : 1;

    const threshold = forkTriggerThreshold ?? 0;
    const barThreshold = threshold + (goalForkRange ?? 0.25);
    const barRange = barFillRange ?? goalForkRange ?? 0.25;

    const [expenseCardHeights, setExpenseCardHeights] = useState<
        [number | null, number | null]
    >([null, null]);
    const updateExpenseCardHeight = useCallback((index: 0 | 1, h: number) => {
        setExpenseCardHeights((prev) => {
            if (prev[index] === h) return prev;
            const next: [number | null, number | null] = [...prev];
            next[index] = h;
            return next;
        });
    }, []);
    const expenseCardCenters =
        expenseCardHeights[0] != null && expenseCardHeights[1] != null
            ? [
                  expenseCardHeights[0] / 2,
                  expenseCardHeights[0] + 8 + expenseCardHeights[1] / 2,
              ]
            : undefined;

    const hasFilledOnce = useSharedValue(false);
    const hasCompletedFill = useSharedValue(false);

    const coreBgStyle = useAnimatedStyle(() => {
        if (!animated || !hasCompletedFill.value) {
            return { backgroundColor: '#F3F4F6' };
        }
        return { backgroundColor: 'rgba(176, 217, 255, 0.75)' };
    });

    const spendBgStyle = useAnimatedStyle(() => {
        if (!animated || !hasCompletedFill.value) {
            return { backgroundColor: '#F3F4F6' };
        }
        return { backgroundColor: 'rgba(97, 188, 118, 0.75)' };
    });

    const coreBarStyle = useAnimatedStyle(() => {
        if (!animated) return { width: '100%', opacity: 1 };
        const dotPhase = Math.max(0, activeProgress.value - 1);
        const linear = Math.min(
            1,
            Math.max(0, (dotPhase - barThreshold) / barRange),
        );
        const t = Easing.inOut(Easing.cubic)(linear);
        if (t > 0) {
            hasFilledOnce.value = true;
            if (t >= 1) {
                hasCompletedFill.value = true;
            }
            return { width: `${t * 100}%`, opacity: 1 };
        }
        if (!hasFilledOnce.value) {
            return { width: '0%', opacity: 0 };
        }
        const fadeOut = 1 - Math.min(1, activeProgress.value * 4);
        return { width: '100%', opacity: fadeOut };
    });

    const spendBarStyle = useAnimatedStyle(() => {
        if (!animated) return { width: '100%', opacity: 1 };
        const dotPhase = Math.max(0, activeProgress.value - 1);
        const linear = Math.min(
            1,
            Math.max(0, (dotPhase - barThreshold) / barRange),
        );
        const t = Easing.inOut(Easing.cubic)(linear);
        if (t > 0) {
            return { width: `${t * 100}%`, opacity: 1 };
        }
        if (!hasFilledOnce.value) {
            return { width: '0%', opacity: 0 };
        }
        const fadeOut = 1 - Math.min(1, activeProgress.value * 4);
        return { width: '100%', opacity: fadeOut };
    });

    return (
        <XStack width="100%" alignItems="center">
            <YStack
                alignItems="center"
                backgroundColor="#F3FAFF"
                padding={6}
                borderRadius={4}
                justifyContent="center"
                minWidth={maxLabelWidth > 0 ? maxLabelWidth : undefined}
                onLayout={(e) => {
                    updateLabelWidth(1, e.nativeEvent.layout.width);
                    updateLabelHeight(
                        'bills',
                        e.nativeEvent.layout.height,
                        e.nativeEvent.layout.y,
                    );
                }}
            >
                <LabelEmphasized
                    textTransform="uppercase"
                    lineHeight={13}
                    letterSpacing={0.5}
                >
                    monthly
                </LabelEmphasized>
                <LabelEmphasized
                    textTransform="uppercase"
                    lineHeight={13}
                    letterSpacing={0.5}
                >
                    expenses
                </LabelEmphasized>
            </YStack>
            <XStack flex={1} alignItems="stretch">
                {braceHeight > 0 && (
                    <OnboardingMoneyMapPreviewFork
                        height={braceHeight}
                        prongs={2}
                        cardCenters={expenseCardCenters}
                        animated
                        loopProgress={animated ? activeProgress : undefined}
                        triggerThreshold={forkTriggerThreshold}
                        forkRange={goalForkRange}
                    />
                )}
                <YStack
                    gap={8}
                    flex={1}
                    marginLeft={8}
                    onLayout={(e) => {
                        setBraceHeight(e.nativeEvent.layout.height);
                    }}
                >
                    <YStack
                        borderRadius={16}
                        backgroundColor="$white"
                        gap={8}
                        padding={8}
                        opacity={coreOpacity}
                        onLayout={(e) => {
                            updateExpenseCardHeight(
                                0,
                                e.nativeEvent.layout.height,
                            );
                        }}
                    >
                        <XStack gap={6} alignItems="center">
                            <Icon icon={Receipt} size={16} />
                            <BodySmallRegular>Core</BodySmallRegular>
                        </XStack>
                        <Animated.View
                            style={[
                                {
                                    borderRadius: 8,
                                    width: '100%',
                                    height: 32,
                                    overflow: 'hidden',
                                    justifyContent: 'center',
                                    paddingHorizontal: 8,
                                },
                                coreBgStyle,
                            ]}
                        >
                            <Animated.View
                                style={[
                                    {
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        backgroundColor: '#B0D9FF',
                                        borderRadius: 8,
                                    },
                                    coreBarStyle,
                                ]}
                            />
                            <LabelEmphasized
                                textTransform="uppercase"
                                lineHeight={13}
                                letterSpacing={0.5}
                                zIndex={1}
                            >
                                {centsToFormattedDollars(
                                    billsTotalCents,
                                    false,
                                )}
                            </LabelEmphasized>
                        </Animated.View>
                    </YStack>
                    <YStack
                        borderRadius={16}
                        backgroundColor="$white"
                        gap={8}
                        padding={8}
                        opacity={spendOpacity}
                        onLayout={(e) => {
                            updateExpenseCardHeight(
                                1,
                                e.nativeEvent.layout.height,
                            );
                        }}
                    >
                        <XStack gap={6} alignItems="center">
                            <Icon icon={CreditCard} size={16} />
                            <BodySmallRegular>Spend</BodySmallRegular>
                        </XStack>
                        <Animated.View
                            style={[
                                {
                                    borderRadius: 8,
                                    width: '100%',
                                    height: 32,
                                    overflow: 'hidden',
                                    justifyContent: 'center',
                                    paddingHorizontal: 8,
                                },
                                spendBgStyle,
                            ]}
                        >
                            <Animated.View
                                style={[
                                    {
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        backgroundColor: '#61BC76',
                                        borderRadius: 8,
                                    },
                                    spendBarStyle,
                                ]}
                            />
                            <LabelEmphasized
                                textTransform="uppercase"
                                lineHeight={13}
                                letterSpacing={0.5}
                                zIndex={1}
                            >
                                {centsToFormattedDollars(
                                    spendTotalCents,
                                    false,
                                )}
                            </LabelEmphasized>
                        </Animated.View>
                    </YStack>
                </YStack>
            </XStack>
        </XStack>
    );
};

export const AnimatedGoalBar = ({
    groupIndex,
    loopProgressA,
    loopProgressB,
    forkThreshold,
    forkRange,
    barFillRange,
}: {
    groupIndex: number;
    loopProgressA?: Animated.SharedValue<number>;
    loopProgressB?: Animated.SharedValue<number>;
    forkThreshold: number;
    forkRange: number;
    barFillRange: number;
}) => {
    const hasFilled = useSharedValue(false);

    const barStyle = useAnimatedStyle(() => {
        if (loopProgressA == null || loopProgressB == null)
            return { width: '100%' };
        if (hasFilled.value) return { width: '100%' };
        const progress =
            groupIndex % 2 === 0 ? loopProgressA.value : loopProgressB.value;
        const dotPhase = Math.max(0, progress - 1);
        const barStart = forkThreshold + forkRange;
        const linear = Math.min(
            1,
            Math.max(0, (dotPhase - barStart) / barFillRange),
        );
        const t = Easing.inOut(Easing.cubic)(linear);
        if (t >= 1) {
            hasFilled.value = true;
        }
        return { width: `${t * 100}%` };
    });

    return (
        <Animated.View
            style={[
                {
                    height: '100%',
                    backgroundColor: '#EEBED4',
                    borderRadius: 8,
                    position: 'absolute',
                    left: 0,
                },
                barStyle,
            ]}
        />
    );
};

export const AnimatedRepeatingGoalBar = ({
    groupIndex,
    filledGoalCount,
    loopProgressA,
    loopProgressB,
    activeLoopIdx,
    forkThreshold,
    forkRange,
    barFillRange,
    totalGroups,
    children,
}: {
    groupIndex: number;
    filledGoalCount: Animated.SharedValue<number>;
    loopProgressA?: Animated.SharedValue<number>;
    loopProgressB?: Animated.SharedValue<number>;
    activeLoopIdx?: Animated.SharedValue<number>;
    forkThreshold: number;
    forkRange: number;
    barFillRange: number;
    totalGroups: number;
    children: ReactNode;
}) => {
    const animated =
        loopProgressA != null && loopProgressB != null && activeLoopIdx != null;
    const hasFilledOnce = useSharedValue(false);
    const hasCompletedFill = useSharedValue(false);

    const bgStyle = useAnimatedStyle(() => {
        if (!animated || !hasCompletedFill.value) {
            return { backgroundColor: '#F3F4F6' };
        }
        return { backgroundColor: 'rgba(238, 190, 212, 0.75)' };
    });

    const barStyle = useAnimatedStyle(() => {
        if (!animated) return { width: '100%', opacity: 1 };

        if (filledGoalCount.value >= totalGroups) {
            // Steady state: repeating fill → hold → fade-out cycle.
            // Evaluate both SVs independently and take the max of
            // widths and opacities so the bar refills every iteration
            // and stays at full width during cross-SV transitions.
            const barStart = forkThreshold + forkRange;
            const fillEnd = barStart + barFillRange;
            const fadeStart = fillEnd + barFillRange * 0.3;
            const fadeRange = barFillRange * 0.7;

            const evalSv = (svVal: number) => {
                'worklet';
                const dp = Math.max(0, svVal - 1);
                const fLin = Math.min(
                    1,
                    Math.max(0, (dp - barStart) / barFillRange),
                );
                const fT = Easing.inOut(Easing.cubic)(fLin);
                const dLin = Math.min(
                    1,
                    Math.max(0, (dp - fadeStart) / fadeRange),
                );
                const dT = Easing.inOut(Easing.cubic)(dLin);
                return { fillT: fT, opacity: fT > 0 ? 1 - dT : 0 };
            };

            const a = evalSv(loopProgressA.value);
            const b = evalSv(loopProgressB.value);

            const width = Math.max(a.fillT, b.fillT);
            const opacity = Math.max(a.opacity, b.opacity);

            if (width > 0 || opacity > 0) {
                hasFilledOnce.value = true;
                if (width >= 1) {
                    hasCompletedFill.value = true;
                }
                return {
                    width: `${width * 100}%`,
                    opacity,
                };
            }
            return { width: '0%', opacity: 0 };
        }

        if (filledGoalCount.value > groupIndex) {
            return { width: '100%', opacity: 1 };
        }

        if (filledGoalCount.value + 1 < groupIndex) {
            return { width: '0%', opacity: 0 };
        }

        // Initial fill: fill once and hold
        const progress =
            groupIndex % 2 === 0 ? loopProgressA.value : loopProgressB.value;
        const dotPhase = Math.max(0, progress - 1);
        const barStart = forkThreshold + forkRange;
        const linear = Math.min(
            1,
            Math.max(0, (dotPhase - barStart) / barFillRange),
        );
        const t = Easing.inOut(Easing.cubic)(linear);
        if (t > 0) {
            hasFilledOnce.value = true;
            if (t >= 1) {
                hasCompletedFill.value = true;
            }
            return { width: `${t * 100}%`, opacity: 1 };
        }
        if (!hasFilledOnce.value) {
            return { width: '0%', opacity: 0 };
        }
        return { width: '100%', opacity: 1 };
    });

    return (
        <Animated.View
            style={[
                {
                    borderRadius: 8,
                    width: '100%',
                    height: 32,
                    overflow: 'hidden',
                    justifyContent: 'center',
                    paddingHorizontal: 8,
                },
                bgStyle,
            ]}
        >
            <Animated.View
                style={[
                    {
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        backgroundColor: '#EEBED4',
                        borderRadius: 8,
                    },
                    barStyle,
                ]}
            />
            {children}
        </Animated.View>
    );
};

export const AnimatedDateLabel = ({
    groupIndex,
    loopProgressA,
    loopProgressB,
    forkThreshold,
    forkRange,
    barFillRange,
    label,
}: {
    groupIndex: number;
    loopProgressA?: Animated.SharedValue<number>;
    loopProgressB?: Animated.SharedValue<number>;
    forkThreshold: number;
    forkRange: number;
    barFillRange: number;
    label: string;
}) => {
    const hasFilled = useSharedValue(false);

    const bgStyle = useAnimatedStyle(() => {
        if (loopProgressA == null || loopProgressB == null) {
            return { backgroundColor: GOALS_FILLED };
        }
        if (hasFilled.value) {
            return { backgroundColor: GOALS_FILLED };
        }
        const progress =
            groupIndex % 2 === 0 ? loopProgressA.value : loopProgressB.value;
        const dotPhase = Math.max(0, progress - 1);
        const barStart = forkThreshold + forkRange;
        const linear = Math.min(
            1,
            Math.max(0, (dotPhase - barStart) / barFillRange),
        );
        const t = Easing.inOut(Easing.cubic)(linear);
        if (t >= 1) {
            hasFilled.value = true;
        }
        return {
            backgroundColor: interpolateColor(
                t,
                [0, 1],
                ['#ffffff', GOALS_FILLED],
            ),
        };
    });

    const textStyle = useAnimatedStyle(() => {
        if (loopProgressA == null || loopProgressB == null) {
            return { color: '#ffffff' };
        }
        if (hasFilled.value) {
            return { color: '#ffffff' };
        }
        const progress =
            groupIndex % 2 === 0 ? loopProgressA.value : loopProgressB.value;
        const dotPhase = Math.max(0, progress - 1);
        const barStart = forkThreshold + forkRange;
        const linear = Math.min(
            1,
            Math.max(0, (dotPhase - barStart) / barFillRange),
        );
        const t = Easing.inOut(Easing.cubic)(linear);
        return {
            color: interpolateColor(t, [0, 1], ['#000000', '#ffffff']),
        };
    });

    return (
        <Animated.View
            style={[
                {
                    borderRadius: 4,
                    paddingHorizontal: 6,
                    paddingVertical: 4,
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1,
                },
                bgStyle,
            ]}
        >
            <Animated.Text
                style={[
                    {
                        textTransform: 'uppercase',
                        lineHeight: 10,
                        letterSpacing: 0.5,
                        fontWeight: '600',
                        fontSize: 10,
                    },
                    textStyle,
                ]}
            >
                {label}
            </Animated.Text>
        </Animated.View>
    );
};

export const GoalsSystemContent = ({
    ruleSetDisplays,
    maxLabelWidth,
    updateLabelWidth,
    updateLabelHeight,
    updateGoalRowHeight,
    hiddenIconTypes,
    loopProgressA,
    loopProgressB,
    activeLoopIdx,
    goalForkThresholds,
    goalDotColor,
    goalForkRange,
    barFillRange,
    filledGoalCount,
    monthlyGoalsCents,
}: SystemBoxLabelProps & {
    ruleSetDisplays: RuleSetDisplay[];
    updateGoalRowHeight: (index: number, height: number) => void;
    hiddenIconTypes?: Set<RuleType>;
    loopProgressA?: Animated.SharedValue<number>;
    loopProgressB?: Animated.SharedValue<number>;
    activeLoopIdx?: Animated.SharedValue<number>;
    goalForkThresholds?: number[];
    goalDotColor?: string;
    goalForkRange?: number;
    barFillRange?: number;
    filledGoalCount: Animated.SharedValue<number>;
    monthlyGoalsCents?: number;
}) => {
    const animated =
        loopProgressA != null && loopProgressB != null && activeLoopIdx != null;

    const allRules = useMemo(
        () => ruleSetDisplays.flatMap((rs) => rs.rules),
        [ruleSetDisplays],
    );
    const debtEffectiveDate = useMemo(() => {
        const debtData = getDebtData(allRules, monthlyGoalsCents);
        if (debtData == null) return null;
        const debtRule = allRules.find((r) => r.type === RuleType.DebtPayDown);
        if (debtRule?.completionDate != null) return null;
        const d = new Date();
        d.setMonth(d.getMonth() + debtData.payoffMonths);
        return d.toLocaleDateString('en-US', {
            month: 'short',
            year: 'numeric',
        });
    }, [allRules, monthlyGoalsCents]);

    const [forkHeights, setForkHeights] = useState<Record<number, number>>({});
    const updateForkHeight = useCallback((index: number, height: number) => {
        setForkHeights((prev) => {
            if (prev[index] === height) return prev;
            return { ...prev, [index]: height };
        });
    }, []);

    const [cardHeights, setCardHeights] = useState<
        Record<number, Record<number, number>>
    >({});
    const updateCardHeight = useCallback(
        (rowIndex: number, cardIndex: number, height: number) => {
            setCardHeights((prev) => {
                const row = prev[rowIndex];
                if (row?.[cardIndex] === height) return prev;
                return {
                    ...prev,
                    [rowIndex]: { ...row, [cardIndex]: height },
                };
            });
        },
        [],
    );

    const getCardCenters = (
        rowIndex: number,
        count: number,
    ): number[] | undefined => {
        const row = cardHeights[rowIndex];
        if (row == null) return undefined;
        const heights: number[] = [];
        for (let c = 0; c < count; c++) {
            const h = row[c];
            if (h == null) return undefined;
            heights.push(h);
        }
        const gap = 8;
        const centers: number[] = [];
        let y = 0;
        for (const h of heights) {
            centers.push(y + h / 2);
            y += h + gap;
        }
        return centers;
    };

    return (
        <YStack gap={8}>
            {ruleSetDisplays.map((ruleSet, i) => {
                const rulesCount = ruleSet.rules.length;
                const prongs = Math.min(rulesCount, 4) as 1 | 2 | 3 | 4;
                const forkHeight = forkHeights[i] ?? 0;
                const retirementIndices = ruleSet.rules.reduce<number[]>(
                    (acc, rule, j) => {
                        if (rule.type === RuleType.Retirement) acc.push(j);
                        return acc;
                    },
                    [],
                );
                const needsFork = rulesCount > 1;

                return (
                    <XStack
                        key={i}
                        width="100%"
                        alignItems="center"
                        onLayout={(e) => {
                            updateGoalRowHeight(i, e.nativeEvent.layout.height);
                        }}
                    >
                        <View
                            backgroundColor="$petalLightest"
                            paddingVertical={6}
                            paddingHorizontal={12}
                            borderRadius={4}
                            alignItems="center"
                            justifyContent="center"
                            minWidth={
                                maxLabelWidth > 0 ? maxLabelWidth : undefined
                            }
                            onLayout={(e) => {
                                updateLabelWidth(2, e.nativeEvent.layout.width);
                                updateLabelHeight(
                                    `goal-${i}`,
                                    e.nativeEvent.layout.height,
                                    e.nativeEvent.layout.y,
                                );
                            }}
                        >
                            <LabelEmphasized
                                textTransform="uppercase"
                                lineHeight={13}
                                letterSpacing={0.5}
                            >
                                goal {i + 1}
                            </LabelEmphasized>
                        </View>
                        {needsFork ? (
                            <View width={48} opacity={forkHeight > 0 ? 1 : 0}>
                                {forkHeight > 0 && (
                                    <OnboardingMoneyMapPreviewFork
                                        height={forkHeight}
                                        prongs={prongs}
                                        cardCenters={getCardCenters(
                                            i,
                                            rulesCount,
                                        )}
                                        animated={animated}
                                        loopProgressA={loopProgressA}
                                        loopProgressB={loopProgressB}
                                        triggerThreshold={
                                            goalForkThresholds?.[i]
                                        }
                                        dotColor={goalDotColor}
                                        forkRange={goalForkRange}
                                        filledCount={filledGoalCount}
                                        groupIndex={i}
                                        alwaysAnimateIndices={retirementIndices}
                                    />
                                )}
                            </View>
                        ) : (
                            <OnboardingMoneyMapPreviewFork
                                height={0}
                                prongs={1}
                                animated={animated}
                                loopProgressA={loopProgressA}
                                loopProgressB={loopProgressB}
                                triggerThreshold={goalForkThresholds?.[i]}
                                dotColor={goalDotColor}
                                forkRange={goalForkRange}
                                filledCount={filledGoalCount}
                                groupIndex={i}
                                alwaysAnimateIndices={retirementIndices}
                            />
                        )}
                        <YStack
                            gap={8}
                            flex={1}
                            marginLeft={8}
                            onLayout={(e) => {
                                if (needsFork) {
                                    updateForkHeight(
                                        i,
                                        e.nativeEvent.layout.height,
                                    );
                                }
                            }}
                        >
                            {ruleSet.rules.map((rule, j) => (
                                <YStack
                                    key={j}
                                    borderRadius={16}
                                    backgroundColor="$white"
                                    gap={8}
                                    padding={8}
                                    onLayout={(e) => {
                                        if (needsFork) {
                                            updateCardHeight(
                                                i,
                                                j,
                                                e.nativeEvent.layout.height,
                                            );
                                        }
                                    }}
                                >
                                    <XStack gap={6} alignItems="center">
                                        <View
                                            opacity={
                                                hiddenIconTypes?.has(rule.type)
                                                    ? 0
                                                    : 1
                                            }
                                        >
                                            <Icon
                                                icon={getRuleIcon(rule.type)}
                                                size={16}
                                            />
                                        </View>
                                        <BodySmallRegular
                                            numberOfLines={1}
                                            flex={1}
                                        >
                                            {rule.description}
                                        </BodySmallRegular>
                                    </XStack>
                                    {rule.type === RuleType.Retirement ? (
                                        <AnimatedRepeatingGoalBar
                                            groupIndex={i}
                                            filledGoalCount={filledGoalCount}
                                            loopProgressA={loopProgressA}
                                            loopProgressB={loopProgressB}
                                            activeLoopIdx={activeLoopIdx}
                                            forkThreshold={
                                                goalForkThresholds?.[i] ?? 0
                                            }
                                            forkRange={goalForkRange ?? 0.25}
                                            barFillRange={
                                                barFillRange ??
                                                goalForkRange ??
                                                0.25
                                            }
                                            totalGroups={ruleSetDisplays.length}
                                        >
                                            <LabelEmphasized
                                                textTransform="uppercase"
                                                lineHeight={13}
                                                letterSpacing={0.5}
                                                zIndex={1}
                                            >
                                                {`${centsToFormattedDollars(rule.yearlyContributionMaxCents ?? 0, false)}/yr`}
                                            </LabelEmphasized>
                                        </AnimatedRepeatingGoalBar>
                                    ) : (
                                        <XStack
                                            borderRadius={8}
                                            width="100%"
                                            backgroundColor="#F3F4F6"
                                            height={32}
                                            alignItems="center"
                                            justifyContent="space-between"
                                            paddingHorizontal={8}
                                            position="relative"
                                        >
                                            <AnimatedGoalBar
                                                groupIndex={i}
                                                loopProgressA={loopProgressA}
                                                loopProgressB={loopProgressB}
                                                forkThreshold={
                                                    goalForkThresholds?.[i] ?? 0
                                                }
                                                forkRange={
                                                    goalForkRange ?? 0.25
                                                }
                                                barFillRange={
                                                    barFillRange ??
                                                    goalForkRange ??
                                                    0.25
                                                }
                                            />
                                            <LabelEmphasized
                                                textTransform="uppercase"
                                                lineHeight={13}
                                                letterSpacing={0.5}
                                                zIndex={1}
                                            >
                                                {centsToFormattedDollars(
                                                    rule.targetBalanceCents ??
                                                        0,
                                                    false,
                                                )}
                                            </LabelEmphasized>
                                            {(rule.completionLabel != null ||
                                                rule.completionDate != null ||
                                                (rule.type ===
                                                    RuleType.DebtPayDown &&
                                                    debtEffectiveDate !=
                                                        null)) && (
                                                <AnimatedDateLabel
                                                    groupIndex={i}
                                                    loopProgressA={
                                                        loopProgressA
                                                    }
                                                    loopProgressB={
                                                        loopProgressB
                                                    }
                                                    forkThreshold={
                                                        goalForkThresholds?.[
                                                            i
                                                        ] ?? 0
                                                    }
                                                    forkRange={
                                                        goalForkRange ?? 0.25
                                                    }
                                                    barFillRange={
                                                        barFillRange ??
                                                        goalForkRange ??
                                                        0.25
                                                    }
                                                    label={
                                                        rule.completionLabel ??
                                                        rule.completionDate ??
                                                        debtEffectiveDate ??
                                                        ''
                                                    }
                                                />
                                            )}
                                        </XStack>
                                    )}
                                </YStack>
                            ))}
                        </YStack>
                    </XStack>
                );
            })}
        </YStack>
    );
};
